import { ref, computed, watch, nextTick } from 'vue';
import { usePageContext } from "vike-vue/usePageContext";
import { useSearchQuery } from './useSearchQuery';
import { LIMIT } from "@repo/modules/api";
import { render } from 'vike/abort'
import type { PageContext } from 'vike/types'
import type { Property, Page, LinkedPage, Agent, AgentWithProfiles, UserProfile, OrganizationProfile, Tag } from '@repo/types';
import type { PageDataBase } from '@repo/ui'

export type Pin = Property & { id: string }

import type { MapParams } from '@repo/ui/composables/map'

export function useSearchData(options: { fetchAgents?: boolean } = {}) {
  const pageContext = usePageContext() as PageContext<PageDataBase>;
  const { api } = pageContext;
  if (!pageContext.data.map) throw render('/500');
  const data = ref(pageContext.data.map);
  const { filters, currentPage, viewMode } = useSearchQuery();
  const listLoading = ref(viewMode.value !== 'grid' && !data.value.place);
  const countLoading = ref(data.value.dealsCount === undefined);
  const labelsLoading = ref(data.value.labels === undefined);

  const total = computed(() => data.value.dealsCount ?? pageContext.data.map?.dealsCount ?? 0);
  const totalPages = computed(() => Math.ceil(total.value / LIMIT));

  async function fetchMissingData() {
    const path = pageContext.routeParams.path;
    if (data.value.dealsCount === undefined) {
      countLoading.value = true;
      api.call<{ dealsCount: number }>("count", path, { filter: filters.value })
        .then(res => { data.value = { ...data.value, dealsCount: res.data.dealsCount } })
        .finally(() => { countLoading.value = false });
    }
    if (data.value.labels === undefined) {
      labelsLoading.value = true;
      api.call<{ labels: Tag[] }>("labels", path, { filter: filters.value })
        .then(res => { data.value = { ...data.value, labels: res.data.labels } })
        .finally(() => { labelsLoading.value = false });
    }
  }

  watch(() => pageContext.data.map, (newMap) => {
    if (newMap) {
      data.value = newMap;
      if (newMap.place && typeof window !== 'undefined') {
        fetchMissingData();
      }
    }
  }, { immediate: true });

  const buildings = computed(() => {
    const points = data.value.buildings ?? [];
    return points
      .filter((b: Property) => b.price || b.rent)
      .map(b => ({
        ...b,
        id: b.geometry.coordinates.toString()
      }));
  });

  const pins = computed<Pin[]>(() => {
    const points = data.value.points ?? data.value.buildings ?? [];
    return points
      .filter((b: Property) => b.price || b.rent)
      .map((b: Property) => ({
        ...b,
        id: b.geometry.coordinates.toString()
      }));
  });

  const place = computed<Page | LinkedPage | null>(() => data.value.place ?? null);

  const agentsWithProfiles = ref<AgentWithProfiles[]>([]);

  const fetchAgentProfiles = async (agents: Agent[]) => {
    if (!options.fetchAgents) return;
    const uniqueAgents = Array.from(new Set(agents.map(a => `${a.organizationId}:${a.id}`)))
      .map(key => {
        const [organizationId, id] = key.split(':');
        return { id, organizationId };
      })
      .filter(a => !agentsWithProfiles.value.some(p => p.id == a.id && p.organizationId == a.organizationId));

    if (uniqueAgents.length === 0) return;

    const profiles = await Promise.all(uniqueAgents.map(async (agent) => {
      try {
        const { data: details } = await api.call<{ agent: UserProfile, company: OrganizationProfile }>('agent', agent.id, agent.organizationId);
        return {
          ...agent,
          profile: details.agent,
          company: details.company
        } as AgentWithProfiles;
      } catch (e) {
        console.error('Failed to fetch agent profile', e);
        return null;
      }
    }));

    const newProfiles = profiles.filter((p): p is AgentWithProfiles => p !== null);
    agentsWithProfiles.value = [...agentsWithProfiles.value, ...newProfiles];
  };

  const getAgentProfilesFor = (agentIds: Agent[]): AgentWithProfiles[] => {
    if (!options.fetchAgents || !agentIds) return [];
    return agentIds.map(a => agentsWithProfiles.value.find(p => p.id == a.id && p.organizationId == a.organizationId)).filter(Boolean) as AgentWithProfiles[];
  };

  watch(buildings, (newBuildings) => {
    if (!options.fetchAgents) return;
    const allAgents = newBuildings.flatMap(b => b.agents || []);
    if (allAgents.length > 0) {
      fetchAgentProfiles(allAgents);
    }
  }, { immediate: true });

  const activeCard = ref<Property | null>(null);

  let timer: ReturnType<typeof setTimeout> | undefined;
  function debounce(fn: () => void, ms = 300) {
    clearTimeout(timer);
    timer = setTimeout(fn, ms);
  }

  async function load(mapParams: MapParams, endpoint = "listings", skip = 0) {
    const { north, south } = mapParams;
    if (north === south) return;
    listLoading.value = true;
    try {
      const res = await api.call(endpoint, pageContext.routeParams.path, {
        skip,
        ...mapParams,
        filter: filters.value,
      });

      data.value = {
        ...data.value,
        ...res.data
      };

      if (endpoint === 'listings') {
        countLoading.value = true;
        api.call<{ dealsCount: number }>("count", pageContext.routeParams.path, {
          ...mapParams,
          filter: filters.value,
        }).then(countRes => {
          data.value = { ...data.value, dealsCount: countRes.data.dealsCount };
        }).finally(() => { countLoading.value = false });

        labelsLoading.value = true;
        api.call<{ labels: Tag[] }>("labels", pageContext.routeParams.path, {
          ...mapParams,
          filter: filters.value,
        }).then(labelsRes => {
          data.value = { ...data.value, labels: labelsRes.data.labels };
        }).finally(() => { labelsLoading.value = false });
      }

      const tmp = activeCard.value;
      activeCard.value = null;
      nextTick(() => {
        activeCard.value = tmp;
      });
    } finally {
      listLoading.value = false;
    }
  }

  return {
    data,
    filters,
    currentPage,
    viewMode,
    listLoading,
    countLoading,
    labelsLoading,
    total,
    totalPages,
    buildings,
    pins,
    place,
    agentsWithProfiles,
    fetchAgentProfiles,
    getAgentProfilesFor,
    activeCard,
    load (...args: Parameters<typeof load>) {
      return debounce(() => load(...args))
    }
  };
}
